import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, ExternalLink } from 'lucide-react';

export const PCAPartnership = () => {
  return (
    <div className="pt-20" data-testid="pca-partnership-page">
      {/* Header */}
      <section className="py-12 bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link 
            to="/newsroom" 
            className="inline-flex items-center text-[#5B4FF9] hover:text-[#4A3FE8] font-medium mb-6 transition-colors"
            data-testid="back-to-newsroom"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Newsroom
          </Link>
          
          <div className="flex items-center gap-4 text-sm text-zinc-500 mb-4">
            <span className="bg-[#5B4FF9] text-white px-3 py-1 rounded-full text-xs font-semibold">
              Partnership
            </span>
            <span className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              August 14, 2026
            </span>
            <span className="flex items-center">
              <MapPin className="h-4 w-4 mr-1" />
              Minneapolis, MN & Oakland, CA
            </span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-extrabold text-[#272926] leading-tight">
            Players Health Foundation and Positive Coaching Alliance Partner to Pair Athlete Safety Grants With Coach Education
          </h1>
        </div>
      </section>

      {/* Content */}
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Lead */}
          <p className="text-xl text-[#5B4FF9] font-semibold mb-8 leading-relaxed">
            Foundation is contributing to PCA and will help cover the cost of PCA's coaching curriculum for its grant recipients
          </p>

          {/* Body */}
          <div className="prose prose-lg max-w-none text-zinc-700 space-y-6">
            <p>
              <strong>MINNEAPOLIS, MN and OAKLAND, CA</strong> — The Players Health Foundation and Positive Coaching Alliance (PCA) today announced a national partnership that connects the Foundation's athlete safety grants and resources with PCA's coach education programming.
            </p>

            <p>
              Under the agreement, the Players Health Foundation is making a contribution to PCA and will help cover the cost of PCA's online coaching curriculum for organizations that receive Foundation grants. The partnership reflects a shared conviction that a safe sports environment is more than a physically safe one. Facilities, protocols, and screening protect the body. Coaching culture shapes everything else: whether a young athlete speaks up when something is wrong, whether they stay in the game, and whether they leave sports with confidence rather than burnout.
            </p>

            {/* Quote 1 */}
            <blockquote className="border-l-4 border-[#5B4FF9] pl-6 py-4 my-8 bg-[#F5F4FF] rounded-r-xl">
              <p className="text-lg italic text-[#272926] mb-4">
                "Almost everything a young athlete takes away from sport comes through their coach. That is where confidence gets built or broken, and it is where a kid decides whether it is safe to speak up. PCA's education is safety education. We wanted it in the hands of the organizations we support, and we did not want cost to be the reason it never got there."
              </p>
              <footer className="text-sm font-semibold text-[#5B4FF9]">
                — Tyrre Burks, President of the Players Health Foundation Board of Directors
              </footer>
            </blockquote>

            <p>
              Through the agreement, the Players Health Foundation is making an unrestricted contribution in support of PCA's mission of ensuring every young person, regardless of social or economic circumstance, has access to a positive youth sports experience. The Foundation will also help cover the cost of seats in PCA's online coaching courses on behalf of its grantees, making coach education a standard component of what a Foundation grant delivers rather than an added cost the grantee has to absorb.
            </p>

            <p>
              For many of the community organizations the Foundation supports, that distinction matters. Coach education is often the first line item cut when budgets tighten, even though it is among the highest-leverage investments a program can make. Removing the cost removes the tradeoff.
            </p>

            {/* Quote 2 */}
            <blockquote className="border-l-4 border-amber-400 pl-6 py-4 my-8 bg-amber-50 rounded-r-xl">
              <p className="text-lg italic text-[#272926] mb-4">
                "Every young athlete deserves a coach who knows how to build confidence, create belonging, and help kids thrive. By partnering with the Players Health Foundation, we're removing barriers to coach education so more organizations—especially those serving under-resourced communities—can provide the positive, safe sports experience every child deserves."
              </p>
              <footer className="text-sm font-semibold text-amber-600">
                — Jason Sacks, Chief Executive Officer of Positive Coaching Alliance
              </footer>
            </blockquote>

            <p>
              Additional program details, including how grantees will access PCA courses, will be shared with Foundation grant recipients directly.
            </p>

            {/* CTA */}
            <div className="bg-[#5B4FF9] rounded-2xl p-8 my-10 text-center">
              <h3 className="text-xl font-bold text-white mb-4">Learn More About Our Grant Program</h3>
              <p className="text-white/90 mb-6">Apply for funding and get access to coach education resources through our partnership with PCA.</p>
              <Link
                to="/grants"
                className="bg-white text-[#5B4FF9] rounded-full px-8 py-3 font-semibold transition-all transform hover:-translate-y-1 shadow-sm hover:shadow-lg inline-flex items-center justify-center"
                data-testid="pca-grants-btn"
              >
                View Grant Program
                <ExternalLink className="ml-2 h-4 w-4" />
              </Link>
            </div>

            {/* About Sections */}
            <div className="border-t border-zinc-200 pt-10 mt-10">
              <h3 className="text-lg font-bold text-[#272926] mb-4">About Players Health Foundation</h3>
              <p className="text-zinc-600 mb-8">
                The Players Health Foundation works to expand access to safe sport for young athletes, with a focus on communities where cost, geography, and infrastructure limit participation. The Foundation provides grants, safety resources, and programming to organizations serving youth and amateur athletes nationwide.
              </p>

              <h3 className="text-lg font-bold text-[#272926] mb-4">About Positive Coaching Alliance</h3>
              <p className="text-zinc-600 mb-8">
                Positive Coaching Alliance is a national nonprofit working to make youth sports positive, equitable, and accessible to all young people. Through coach and parent education, organizational partnerships, and community initiatives, PCA equips the adults in youth sports to create environments where young athletes develop confidence, character, and a lasting love of the game.
              </p>

              {/* Media Contacts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-zinc-100 rounded-xl p-6">
                  <h4 className="text-sm font-bold text-zinc-500 uppercase tracking-wider mb-3">Players Health Foundation</h4>
                  <p className="text-[#272926] font-semibold">Kyle Lubrano</p>
                  <p className="text-zinc-600 text-sm mb-2">Vice President, Board of Directors</p>
                  <a href="mailto:kyle.lubrano@playershealth.com" className="text-[#5B4FF9] hover:underline text-sm">
                    kyle.lubrano@playershealth.com
                  </a>
                </div>
                <div className="bg-zinc-100 rounded-xl p-6">
                  <h4 className="text-sm font-bold text-zinc-500 uppercase tracking-wider mb-3">Positive Coaching Alliance</h4>
                  <p className="text-[#272926] font-semibold">Casey U'Ren</p>
                  <p className="text-zinc-600 text-sm mb-2">Vice President, External Relations</p>
                  <a href="mailto:casey_uren@positivecoach.org" className="text-[#5B4FF9] hover:underline text-sm">
                    casey_uren@positivecoach.org
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};
